/**
 * Öffentliche Seite „Als Gruppe anfragen“ (KEIN Login) — Ziel des zweiten Knopfs der Saison-Mails
 * (Antoines Entscheidungen vom 26.09.2026). Grundsatz: ein Angebot führt entweder zu einer
 * Reservierung über OpenTable oder zu einer Gruppenbuchung über MAESTRO.
 *
 * - Ab GRUPPE_AB Personen entsteht eine Anfrage über denselben Eingang wie jedes Website-Formular
 *   (POST /api/public/inquiries, sourceDetail `saison-gruppenanfrage`) — samt Eingangsbestätigung,
 *   automatischem Entwurf und Zuordnung zur Vormerkung über die E-Mail-Adresse (Stufe 3).
 * - Darunter verweist die Seite auf OpenTable (Link aus den Einstellungen, GET /api/public/betrieb)
 *   und schickt nichts ab.
 * Der Link aus der Mail trägt nur Anlass, Jahr, Sprache und Paketnamen — keine Adresse, keine Kennung.
 */
import { useState } from "react";
import { Button, ButtonLink, DateInput, Icon } from "../ui/components";
import { GastRahmen, useBetriebsMarke } from "../ui/BetriebsKopf";
import { GRUPPE_AB, anlassTag } from "../ui/SaisonKampagneDrawer";

const ANLASS: Record<string, { de: string; en: string }> = {
  valentinstag: { de: "Valentinstag", en: "Valentine's Day" },
  ostermontag: { de: "Ostermontag", en: "Easter Monday" },
  weihnachten: { de: "Weihnachten", en: "Christmas" },
  silvester: { de: "Silvester", en: "New Year's Eve" },
};

const T = {
  de: {
    titel: "Als Gruppe anfragen",
    intro: (a: string | null) => `${a ? `${a}: ` : ""}Ab ${GRUPPE_AB} Personen stellen wir Ihnen gern ein Angebot zusammen.`,
    personen: "Personen",
    klein: `Für bis zu ${GRUPPE_AB - 1} Personen reservieren Sie Ihren Tisch am schnellsten direkt online.`,
    tisch: "Tisch reservieren",
    kleinOhneLink: `Für bis zu ${GRUPPE_AB - 1} Personen reservieren Sie bitte direkt beim Restaurant.`,
    name: "Name",
    email: "E-Mail",
    telefon: "Telefon (optional)",
    datum: "Datum",
    uhrzeit: "Uhrzeit (optional)",
    nachricht: "Nachricht (optional)",
    menue: "Menü",
    senden: "Gruppenanfrage senden",
    sendet: "Wird gesendet …",
    fehler: "Die Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.",
    dankeTitel: "Vielen Dank!",
    danke: "Ihre Gruppenanfrage ist bei uns. Wir melden uns mit einem passenden Vorschlag.",
  },
  en: {
    titel: "Group request",
    intro: (a: string | null) => `${a ? `${a}: ` : ""}For groups of ${GRUPPE_AB} or more, we are happy to put together an offer for you.`,
    personen: "Guests",
    klein: `For up to ${GRUPPE_AB - 1} guests, the quickest way is to book your table online.`,
    tisch: "Book a table",
    kleinOhneLink: `For up to ${GRUPPE_AB - 1} guests, please book directly with the restaurant.`,
    name: "Name",
    email: "Email",
    telefon: "Phone (optional)",
    datum: "Date",
    uhrzeit: "Time (optional)",
    nachricht: "Message (optional)",
    menue: "Menu",
    senden: "Send group request",
    sendet: "Sending …",
    fehler: "We couldn't send your request. Please try again.",
    dankeTitel: "Thank you!",
    danke: "We have received your group request and will get back to you with a suitable proposal.",
  },
} as const;

export default function GruppenanfragePage() {
  const q = new URLSearchParams(window.location.search);
  const lang: "de" | "en" = q.get("lang") === "en" ? "en" : "de";
  const t = T[lang];
  const anlass = ANLASS[q.get("anlass") ?? ""] ? (q.get("anlass") as string) : null;
  const jahr = Number(q.get("jahr")) || null;
  const paket = (q.get("paket") ?? "").slice(0, 200) || null;
  const anlassText = anlass ? `${ANLASS[anlass]![lang]}${jahr ? ` ${jahr}` : ""}` : null;
  const opentable = useBetriebsMarke()?.opentable_url ?? null;

  const [f, setF] = useState({
    personen: "",
    name: "",
    email: "",
    telefon: "",
    datum: anlass && jahr ? anlassTag(anlass, jahr) : "",
    uhrzeit: "",
    nachricht: "",
    website: "", // Honigtopf
  });
  const [phase, setPhase] = useState<"form" | "sendet" | "fertig">("form");
  const [fehler, setFehler] = useState<string | null>(null);
  const setze = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((alt) => ({ ...alt, [k]: e.target.value }));

  const personen = Number(f.personen);
  const zuKlein = f.personen !== "" && personen > 0 && personen < GRUPPE_AB;
  const bereit = personen >= GRUPPE_AB && f.name.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim());

  async function senden() {
    if (!bereit) return;
    setPhase("sendet");
    setFehler(null);
    try {
      const res = await fetch("/api/public/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerName: f.name.trim(),
          customerEmail: f.email.trim(),
          phone: f.telefon.trim() || undefined,
          guests: personen,
          eventDate: f.datum ? `${f.datum}T00:00:00.000Z` : undefined,
          eventTime: f.uhrzeit || undefined,
          eventType: anlassText ?? undefined,
          message: f.nachricht.trim() || undefined,
          packageName: paket ?? undefined,
          serviceKind: "event",
          sourceDetail: "saison-gruppenanfrage",
          language: lang,
          website: f.website || undefined,
        }),
      });
      if (!res.ok) throw new Error(t.fehler);
      setPhase("fertig");
    } catch {
      setFehler(t.fehler);
      setPhase("form");
    }
  }

  const feld = "input w-full";
  const label = "mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";

  return (
    <GastRahmen>
      {phase === "fertig" ? (
        <div className="text-center" data-testid="gruppenanfrage-danke">
          <Icon name="check_circle" className="mx-auto mb-3 text-[40px] text-tertiary dark:text-tertiary-fixed-dim" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">{t.dankeTitel}</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{t.danke}</p>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void senden();
          }}
        >
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">{t.titel}</h1>
            <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{t.intro(anlassText)}</p>
            {paket && (
              <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {t.menue}: {paket}
              </p>
            )}
          </div>

          <label className="block">
            <span className={label}>{t.personen}</span>
            <input className={feld} type="number" inputMode="numeric" min={1} max={500} value={f.personen} onChange={setze("personen")} required />
          </label>

          {zuKlein ? (
            <div data-testid="gruppenanfrage-opentable" className="flex flex-col gap-3 rounded-xl border border-outline-variant bg-surface-container-low px-4 py-3.5">
              <p className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{opentable ? t.klein : t.kleinOhneLink}</p>
              {opentable && (
                <ButtonLink href={opentable} className="w-full !py-3 text-center" rel="noopener noreferrer">
                  {t.tisch}
                </ButtonLink>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className={label}>{t.name}</span>
                  <input className={feld} value={f.name} onChange={setze("name")} autoComplete="name" required maxLength={200} />
                </label>
                <label className="block">
                  <span className={label}>{t.email}</span>
                  <input className={feld} type="email" value={f.email} onChange={setze("email")} autoComplete="email" required maxLength={320} />
                </label>
              </div>
              <label className="block">
                <span className={label}>{t.telefon}</span>
                <input className={feld} type="tel" value={f.telefon} onChange={setze("telefon")} autoComplete="tel" maxLength={60} />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className={label}>{t.datum}</span>
                  <DateInput className="w-full" value={f.datum} onValueChange={(v) => setF((alt) => ({ ...alt, datum: v }))} />
                </label>
                <label className="block">
                  <span className={label}>{t.uhrzeit}</span>
                  <input className={feld} type="time" value={f.uhrzeit} onChange={setze("uhrzeit")} />
                </label>
              </div>
              <label className="block">
                <span className={label}>{t.nachricht}</span>
                <textarea className={`${feld} min-h-[110px] resize-y`} value={f.nachricht} onChange={setze("nachricht")} maxLength={5000} />
              </label>
              {/* Honigtopf: für Menschen unsichtbar, Bots füllen ihn (Eingang antwortet dann still). */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={setze("website")} />
              {fehler && <p className="rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{fehler}</p>}
              <Button type="submit" className="w-full !py-4" disabled={!bereit || phase === "sendet"}>
                {phase === "sendet" ? t.sendet : t.senden}
              </Button>
            </>
          )}
        </form>
      )}
    </GastRahmen>
  );
}
